import { useEffect, useRef, useState } from "react";
import { getFileDetails } from "../../services/api";

const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const useSenderImages = ({
  messages = [],
  senderIdKey,
  senderImageKey,
  headers,
  dispatch,
  resetKey
}) => {
  const [senderImages, setSenderImages] = useState({});
  const senderImageCacheRef = useRef({});
  const pendingSenderImagesRef = useRef({});

  useEffect(() => {
    let isActive = true;
    const nextSenderImages = {};
    const requiredSenders = new Map();

    messages.forEach((message) => {
      const senderID = message?.[senderIdKey];
      const senderImage = message?.[senderImageKey];
      if (!senderID || !senderImage) return;

      try {
        const parsedImage = typeof senderImage === "string"
          ? JSON.parse(senderImage)
          : senderImage;

        if (parsedImage?.key && SUPPORTED_IMAGE_TYPES.includes(parsedImage.type)) {
          requiredSenders.set(String(senderID), {
            senderID: String(senderID),
            key: parsedImage.key,
          });
        }
      } catch (error) {
        console.warn('Malformed image payload:', error);
      }
    });

    Object.entries(senderImageCacheRef.current).forEach(([senderID, cachedImage]) => {
      const requiredImage = requiredSenders.get(senderID);

      if (!requiredImage || requiredImage.key !== cachedImage.key) {
        URL.revokeObjectURL(cachedImage.url);
        delete senderImageCacheRef.current[senderID];
      } else {
        nextSenderImages[senderID] = cachedImage.url;
      }
    });

    setSenderImages(nextSenderImages);

    requiredSenders.forEach(({ senderID, key }) => {
      if (
        senderImageCacheRef.current[senderID]?.key === key ||
        pendingSenderImagesRef.current[senderID] === key
      ) return;

      pendingSenderImagesRef.current[senderID] = key;

      getFileDetails(key, headers, dispatch, (response) => {
        delete pendingSenderImagesRef.current[senderID];
        if (!isActive || response.result !== "SUCCESS") return;

        const imageUrl = URL.createObjectURL(response.data);
        const existingImage = senderImageCacheRef.current[senderID];

        if (existingImage?.url) {
          URL.revokeObjectURL(existingImage.url);
        }

        senderImageCacheRef.current[senderID] = { key, url: imageUrl };
        setSenderImages((prev) => ({ ...prev, [senderID]: imageUrl }));
      });
    });

    return () => {
      isActive = false;
    };
  }, [dispatch, headers, messages, senderIdKey, senderImageKey]);

  useEffect(() => () => {
    Object.values(senderImageCacheRef.current).forEach(({ url }) => {
      URL.revokeObjectURL(url);
    });
    senderImageCacheRef.current = {};
    pendingSenderImagesRef.current = {};
    setSenderImages({});
  }, [resetKey]);

  return senderImages;
};

export default useSenderImages;
